/**
 * [rev16 Phase A] SecondaryFieldsDisclosure — "More ▾" collapsible for secondary fields
 *
 * Renders the canonical `SECONDARY_FIELDS` (currency, discountAmount, ...) in
 * a collapsible disclosure that opens onto the same `<TransactionFieldRow>`
 * primitive used by the primary fields. Both Detail and Edit/Add modals
 * consume this component so the disclosure pattern is consistent across
 * modes.
 *
 * Mode-specific value plumbing is explicit:
 *   - `mode === 'edit'` requires `form`; values come from `form.watch(...)`.
 *   - `mode === 'read'` requires `values` keyed by `field.key`. The component
 *     does NOT invent a transaction lookup — the caller passes the value map
 *     it already owns. This avoids leaking `Transaction` shape into a leaf
 *     component and keeps the contract compile-safe.
 *
 * The disclosure trigger is a real `<button>` with `aria-expanded` and
 * `aria-controls`, carries a 44px touch target, and never relies on keyboard
 * focus on a raw `div`.
 *
 * Stable test IDs:
 *   - `data-testid="secondary-fields"` — the wrapping container.
 *   - `data-testid="secondary-fields-toggle"` — the disclosure trigger.
 *   - `data-testid="secondary-fields-body"` — the body (only when open).
 *
 * @see docs/superpowers/plans/2026-08-16-rev16-modal-redesign-plan.md (Section 4.3)
 */
import * as React from 'react';
import { useState, useEffect } from 'react';
import type { UseFormReturn } from 'react-hook-form';

import { cn } from '@/lib/utils';
import {
  TransactionFieldRow,
  type FieldMode,
  type FieldSpec,
} from './TransactionFieldRow';
import type { TransactionFormData } from './TransactionForm';

/**
 * Read-mode value map keyed by `field.key`. The component does not look up
 * the underlying transaction; the caller owns that mapping.
 */
export type SecondaryFieldsValueMap = Readonly<Record<string, unknown>>;

export interface SecondaryFieldsDisclosureProps {
  /** Field specs to render inside the disclosure body. */
  fields: ReadonlyArray<FieldSpec>;
  /** Render mode. */
  mode: FieldMode;
  /**
   * Edit-mode form. Required when `mode === 'edit'`; omitted when `mode === 'read'`.
   */
  form?: UseFormReturn<TransactionFormData>;
  /**
   * Read-mode value map keyed by `field.key`. Required when `mode === 'read'`;
   * omitted when `mode === 'edit'`. The contract is explicit so the component
   * never invents a transaction lookup or relies on a loose `any`/cast.
   */
  values?: SecondaryFieldsValueMap;
  /**
   * Optional per-row shortcut handler (Option Y). Only emits in read mode.
   * The caller decides which rows open the Edit modal and how to scroll.
   */
  onEditShortcut?: (fieldKey: string) => void;
  /**
   * Optional initial open state. Defaults to `false` (collapsed). Edit mode
   * typically starts closed; read mode often starts open so all advanced
   * fields are visible at once.
   */
  defaultOpen?: boolean;
  /**
   * [rev21 P6] Optional signal that the disclosure should open (e.g. when the
   * caller detects a non-zero discount). Initializes `isOpen` to true when set
   * and re-opens the disclosure whenever the value flips back to true. The user
   * retains full toggle control — `autoOpen` never closes a user-opened panel.
   */
  autoOpen?: boolean;
  /** Optional extra class names for the wrapping container. */
  className?: string;
}

/**
 * Type-safe read of the value map. Returns `undefined` when the key is missing
 * so the underlying `<TransactionFieldRow>` can render its own empty state.
 */
function readValue(map: SecondaryFieldsValueMap | undefined, key: string): unknown {
  if (!map) return undefined;
  return Object.prototype.hasOwnProperty.call(map, key) ? map[key] : undefined;
}

export function SecondaryFieldsDisclosure(
  props: SecondaryFieldsDisclosureProps,
): React.ReactElement | null {
  const {
    fields,
    mode,
    form,
    values,
    onEditShortcut,
    defaultOpen = false,
    autoOpen = false,
    className,
  } = props;

  const [isOpen, setIsOpen] = useState<boolean>(defaultOpen || autoOpen);
  const bodyId = React.useId();
  // [rev21 P6] Auto-open when caller signals (e.g. non-zero discount). The effect
  // only opens; it never closes a user-controlled disclosure when autoOpen flips
  // back to false — user toggle remains authoritative.
  useEffect(() => {
    if (autoOpen) setIsOpen(true);
  }, [autoOpen]);

  if (fields.length === 0) {
    return null;
  }

  // Mode guards — compile-time regression check (mirrors TransactionFieldRow).
  if (mode === 'edit' && !form) {
    throw new Error(
      "SecondaryFieldsDisclosure: 'form' is required when mode === 'edit'.",
    );
  }
  if (mode === 'read' && !values) {
    throw new Error(
      "SecondaryFieldsDisclosure: 'values' is required when mode === 'read'.",
    );
  }

  const toggleLabel = `${isOpen ? 'Hide' : 'Show'} ${fields.length} advanced field${
    fields.length === 1 ? '' : 's'
  }`;

  return (
    <div
      data-testid="secondary-fields"
      className={cn('border-t border-border', className)}
    >
      <button
        type="button"
        data-testid="secondary-fields-toggle"
        aria-expanded={isOpen}
        aria-controls={bodyId}
        onClick={() => setIsOpen((prev) => !prev)}
        className={cn(

          'w-full min-h-[44px] py-2 px-1 inline-flex items-center justify-between gap-2 text-left',

          'text-sm text-muted-foreground hover:text-foreground/80',

          'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md',

        )}

      >

        <span className="min-w-0 flex-1 text-left font-medium break-words [overflow-wrap:anywhere] leading-snug">

          {isOpen ? '▾ Less' : '▸ More'}

        </span>

        <span className="shrink-0 whitespace-nowrap pl-2 text-xs text-muted-foreground">

          {fields.length} advanced field{fields.length === 1 ? '' : 's'}

        </span>
      </button>

      {isOpen ? (
        <div
          id={bodyId}
          data-testid="secondary-fields-body"
          className="space-y-0 pb-2"
        >
          {fields.map((spec) => {
            const isReadMode = mode === 'read';
            const fieldValue = isReadMode ? readValue(values, spec.key) : undefined;
            return (
              <TransactionFieldRow
                key={spec.key}
                field={spec}
                mode={mode}
                {...(isReadMode
                  ? {
                      value: fieldValue,
                      onEditShortcut: onEditShortcut
                        ? () => onEditShortcut(spec.key)
                        : undefined,
                    }
                  : { form, value: fieldValue })}
              />
            );
          })}
        </div>
      ) : null}
      <span className="sr-only">{toggleLabel}</span>
    </div>
  );
}

export default SecondaryFieldsDisclosure;
